import { useCallback } from 'react';

import type { CohortSource, ModalName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import type { AIMessageScreen } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { useMessagingTrackingData } from 'bundles/enterprise-admin-messaging-v2/hooks/useMessagingTrackingData';
import type { PreviewReturnScreen } from 'bundles/enterprise-admin-messaging-v2/utils/constants';
import { PREVIEW_RETURN_SCREEN } from 'bundles/enterprise-admin-messaging-v2/utils/constants';
import { MESSAGING_BUTTON, MESSAGING_MODAL } from 'bundles/enterprise-admin-messaging-v2/utils/eventing';

/** Everything needed to attribute a send's `modal.name` and `cohortSource`. */
export type SendContext = {
  /** Screen the admin sent from (drives `modal.name`). */
  activeScreen: AIMessageScreen;
  /** When sending from Preview, which screen Preview was opened from. */
  previewReturnScreen: PreviewReturnScreen;
  /** Whether the cohort/content was manually edited before sending. */
  isManuallyEdited: boolean;
  /**
   * How the audience was sourced. `filter` and `selection` are fixed-list entry
   * points (funnel reminders, learner-table picks) → `preset_reminder`.
   * Absent or `aiCohort` → the free-text AI compose path.
   */
  audienceSourceKind?: 'aiCohort' | 'filter' | 'selection';
};

/** The `modal.name` the send is attributed to, derived from the active screen. */
const deriveSendModal = (activeScreen: AIMessageScreen): ModalName => {
  if (activeScreen === 'manual') {
    return MESSAGING_MODAL.MANUAL_COMPOSE;
  }
  if (activeScreen === 'preview') {
    return MESSAGING_MODAL.PREVIEW_EMAIL;
  }
  return MESSAGING_MODAL.CREATE_STEP2;
};

/**
 * Provenance of the cohort + content being sent. Precedence:
 *   1. Manual compose / CSV (incl. previewing from the manual screen) → `manual`.
 *   2. Fixed-list entry points (preset filters, learner-table selections) → `preset_reminder`.
 *   3. AI compose that was manually edited before sending → `ai_edited`.
 *   4. Default AI compose → `ai_generated`.
 */
const deriveCohortSource = ({
  activeScreen,
  previewReturnScreen,
  isManuallyEdited,
  audienceSourceKind,
}: SendContext): CohortSource => {
  const fromManual =
    activeScreen === 'manual' || (activeScreen === 'preview' && previewReturnScreen === PREVIEW_RETURN_SCREEN.MANUAL);
  if (fromManual) {
    return 'manual';
  }
  if (audienceSourceKind === 'filter' || audienceSourceKind === 'selection') {
    return 'preset_reminder';
  }
  return isManuallyEdited ? 'ai_edited' : 'ai_generated';
};

/**
 * Tracking for the AIMessageModal orchestrator. Keeps the modal's event wiring
 * (close-modal impressions + the two-part send marker) out of the component so
 * the orchestration logic stays readable. Reads the shared messaging tracking
 * entities from {@link useMessagingTrackingData} and spreads them into each event.
 */
const useAIMessageModalTracking = () => {
  const track = useTracker();
  const trackingData = useMessagingTrackingData();

  /** Fire `close_modal` for the given modal. */
  const trackCloseModal = useCallback<(modalName: ModalName) => void>(
    (modalName) => {
      track('close_modal', { ...trackingData, modal: { name: modalName } });
    },
    [track, trackingData]
  );

  /**
   * Fire the Send click marker (`confirm_send`), attributing it to the
   * originating coach message when a `coachMessageId` is supplied (e.g. a
   * recommended-action entry point). `coach` lives on `click_button`, so it is
   * attached to this marker, not the dedicated send marker. Returns a callback
   * that fires the dedicated `send_enterprise_admin_message` marker once the
   * send actually completes — so the dedicated marker is only emitted on success.
   */
  const trackSend = useCallback<(context: SendContext, coachMessageId?: string) => () => void>(
    (context, coachMessageId) => {
      const sendModal = deriveSendModal(context.activeScreen);
      const cohortSource = deriveCohortSource(context);
      track('click_button', {
        button: { name: MESSAGING_BUTTON.CONFIRM_SEND },
        ...trackingData,
        ...(coachMessageId ? { coach: { chatMessageId: coachMessageId } } : {}),
        modal: { name: sendModal },
      });
      return () => {
        track('send_enterprise_admin_message', {
          cohortSource,
          ...trackingData,
          modal: { name: sendModal },
        });
      };
    },
    [track, trackingData]
  );

  return { trackCloseModal, trackSend };
};

export default useAIMessageModalTracking;
